import { useRef, useState } from 'react'
import { Button, FileDropzone, Input, StepIndicator } from '@rb/ui'
import type { QualityReport, UploadedFile } from '@rb/ui'
import styles from './ProjectNew.module.css'

const STEPS = [{ label: 'Dateien' }, { label: 'E-Mail' }, { label: 'Bestätigung' }]

export default function ProjectNew() {
  const [step, setStep] = useState(1)
  const [projectName, setProjectName] = useState('')
  const [files, setFiles] = useState<UploadedFile[]>([])
  const [filesUploaded, setFilesUploaded] = useState(false)
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [projectId, setProjectId] = useState('')
  const [tempToken, setTempToken] = useState('')
  const idCounter = useRef(0)

  function addFiles(newFiles: File[]) {
    setFiles((prev) => [
      ...prev,
      ...newFiles.map((f) => ({ id: String(++idCounter.current), file: f })),
    ])
    if (filesUploaded) setFilesUploaded(false)
  }

  function removeFile(id: string) {
    setFiles((prev) => prev.filter((f) => f.id !== id))
    if (filesUploaded) setFilesUploaded(false)
  }

  async function handleUploadAndCheck() {
    setError('')
    setLoading(true)
    try {
      const form = new FormData()
      form.append('project_name', projectName.trim())
      for (const f of files) form.append('files', f.file)

      const res = await fetch('/api/projects/create-anonymous', {
        method: 'POST',
        body: form,
      })
      const data = (await res.json()) as {
        project_id: string
        temp_token: string
        files: Array<{ id: string; filename: string; quality_report: QualityReport }>
        error?: string
      }
      if (!res.ok) {
        setError(data.error ?? 'Upload fehlgeschlagen')
        return
      }
      setProjectId(data.project_id)
      setTempToken(data.temp_token)
      setFiles((prev) =>
        prev.map((f) => {
          const uploaded = data.files.find((uf) => uf.filename === f.file.name)
          return uploaded ? { ...f, qualityReport: uploaded.quality_report } : f
        })
      )
      setFilesUploaded(true)
    } catch {
      setError('Verbindungsfehler. Bitte erneut versuchen.')
    } finally {
      setLoading(false)
    }
  }

  async function handleClaim() {
    setError('')
    setLoading(true)
    try {
      const res = await fetch('/api/projects/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: projectId,
          temp_token: tempToken,
          email: email.trim(),
        }),
      })
      const data = (await res.json()) as {
        ok: boolean
        existing_user: boolean
        error?: string
      }
      if (!res.ok) {
        setError(data.error ?? 'Fehler beim Senden der E-Mail')
        return
      }
      setStep(3)
    } catch {
      setError('Verbindungsfehler. Bitte erneut versuchen.')
    } finally {
      setLoading(false)
    }
  }

  async function handleResend() {
    setLoading(true)
    try {
      await fetch('/api/projects/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId, temp_token: tempToken, email }),
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <StepIndicator steps={STEPS} current={step - 1} />

        {step === 1 && (
          <div className={styles.stepContent}>
            <h1 className={styles.heading}>Neues Projekt anlegen</h1>
            <Input
              label="Projektname"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="z. B. Betrieb München Nord"
              disabled={filesUploaded}
              required
            />
            <FileDropzone
              files={files}
              onFilesAdded={addFiles}
              onFileRemoved={removeFile}
              disabled={filesUploaded}
            />
            {filesUploaded && (
              <div className={styles.uploadedNote}>
                Dateien geprüft — bitte weiter zu Schritt 2
              </div>
            )}
            {error && <p className={styles.error}>{error}</p>}
            <Button
              onClick={filesUploaded ? () => setStep(2) : handleUploadAndCheck}
              disabled={loading || files.length === 0 || !projectName.trim()}
              style={{ width: '100%' }}
            >
              {loading ? 'Wird hochgeladen…' : 'Weiter'}
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className={styles.stepContent}>
            <h1 className={styles.heading}>Wohin sollen wir Ihre Analyse schicken?</h1>
            <p className={styles.subheading}>
              Wir benachrichtigen Sie, sobald Ihr Lastgang analysiert wurde.
            </p>
            <Input
              type="email"
              label="E-Mail-Adresse"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ihre@email.at"
              autoComplete="email"
              required
            />
            {error && <p className={styles.error}>{error}</p>}
            <Button
              onClick={handleClaim}
              disabled={loading || !email.trim()}
              style={{ width: '100%' }}
            >
              {loading ? 'Wird gesendet…' : 'Analyse anfordern'}
            </Button>
          </div>
        )}

        {step === 3 && (
          <div className={styles.stepContent}>
            <h1 className={styles.heading}>Bitte bestätigen Sie Ihre E-Mail-Adresse</h1>
            <p className={styles.body}>
              Wir haben eine E-Mail an{' '}
              <span className={styles.emailHighlight}>{email}</span> gesendet. Klicken Sie auf
              den Link in der E-Mail, um Ihr Konto einzurichten und zur Analyse zu gelangen.
            </p>
            <Button
              variant="secondary"
              onClick={handleResend}
              disabled={loading}
              style={{ width: '100%' }}
            >
              {loading ? 'Wird gesendet…' : 'E-Mail erneut senden'}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
